"use client";

import type { ReactNode } from "react";
import {
  Tabs as ReactAriaTabs,
  TabList,
  Tab,
  TabPanel,
} from "react-aria-components";
import type { TabsProps } from "react-aria-components";
import { cn } from "../../utilities/tailwind";

export type TabProperties = { label: ReactNode; id: string; panel: ReactNode };
export type TabsProperties = {
  /**
   * Class name to be applied to the exterior Tabs component
   */
  className?: string | undefined;

  /**
   * Class name to be applied to and individual Tab
   */
  tabClassName?: string | undefined;

  /**
   * Class name to be applied to TabPanel
   */
  tabPanelClassName?: string | undefined;

  /**
   * The tabs are made up of TabProperties, which require a `label: ReactNode`, `id: string`, and `panel: ReactNode`.
   */
  tabs: TabProperties[];

  /**
   * Theme that will toggle the text color of the Tab
   */
  theme?: "dark" | "light" | undefined;
} & Omit<
  TabsProps,
  | "children"
  | "className"
  | "download"
  | "href"
  | "hrefLang"
  | "id"
  | "orientation"
  | "ping"
  | "referrerPolicy"
  | "rel"
  | "routerOptions"
  | "style"
  | "target"
>;
export const Tabs = ({
  className = "",
  tabClassName = "",
  tabPanelClassName = "",
  tabs,
  theme = "light",
  ...properties
}: TabsProperties) => {
  const twClassName = cn("w-full", className);

  const twTabsClassName = cn(
    "relative flex cursor-pointer pb-2 text-body whitespace-nowrap text-primary-variant focus-outline after:absolute after:inset-x-0 after:-bottom-px after:mx-auto after:h-1 after:w-0 after:bg-secondary after:transition-all after:content-['_'] aria-selected:after:w-full data-[is-last-tab=false]:me-5 md:data-[is-last-tab=false]:me-8",
    tabClassName,
  );

  const twTabPanelsClassName = cn("focus-outline", tabPanelClassName);

  return (
    <ReactAriaTabs className={twClassName} {...properties}>
      <div className="overflow-x-auto p-1">
        <TabList className="mx-auto flex w-fit border-b border-b-outline">
          {tabs.map(({ id, label }, index) => (
            <Tab
              className={twTabsClassName}
              data-is-last-tab={index === tabs.length - 1}
              data-theme={theme}
              id={id}
              key={id}
            >
              {label}
            </Tab>
          ))}
        </TabList>
      </div>

      {tabs.map(({ id, panel }) => (
        <TabPanel
          className={twTabPanelsClassName}
          data-theme={theme}
          id={id}
          key={id}
        >
          {panel}
        </TabPanel>
      ))}
    </ReactAriaTabs>
  );
};

Tabs.displayName = "Tabs";
